import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Form,
  Grid,
  Header,
  Card,
  Statistic,
  Divider,
  Modal,
  Image,
  Segment,
  Radio,
  Message,
} from 'semantic-ui-react';
import { API, showError, showInfo, showSuccess } from '../../helpers';
import { renderQuota } from '../../helpers/render';
import { useTranslation } from 'react-i18next';

const TopUp = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [redemptionCode, setRedemptionCode] = useState('');
  const [topUpLink, setTopUpLink] = useState('');
  const [userQuota, setUserQuota] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]); // 充值历史记录
  
  // 充值弹窗状态
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [paymentMethod, setPaymentMethod] = useState('alipay'); // 'alipay' or 'wechat'
  const [qrCode, setQrCode] = useState('');
  const [orderId, setOrderId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, paid, failed
  const [countdown, setCountdown] = useState(1800); // 30分钟倒计时
  const timerRef = useRef(null);
  const pollTimerRef = useRef(null);
  
  // 充值金额选项
  const amountOptions = [
    { value: 0.01, label: '¥0.01', quota: '0.01 quota' }, // 测试金额
    { value: 100, label: '¥100', quota: '100 quota' },
    { value: 200, label: '¥200', quota: '200 quota' },
    { value: 300, label: '¥300', quota: '300 quota' },
    { value: 400, label: '¥400', quota: '400 quota' },
    { value: 500, label: '¥500', quota: '500 quota' },
  ];

  const topUp = async () => {
    if (redemptionCode === '') {
      showInfo(t('topup.redeem_code.empty_code'));
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await API.post('/api/user/topup', {
        key: redemptionCode,
      });
      const { success, message, data } = res.data;
      if (success) {
        showSuccess(t('topup.redeem_code.success'));
        setUserQuota((quota) => {
          return quota + data;
        });
        setRedemptionCode('');
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('topup.redeem_code.request_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openTopUpLink = () => {
    // 英文环境跳转到联系我们页面
    if (currentLanguage === 'en') {
      window.location.href = '/contact-us';
    } else {
      // 中文环境打开支付弹窗
      setPaymentModalOpen(true);
    }
  };

  const handlePayment = async () => {
    try {
      const res = await API.post('/api/payment/create', {
        amount: selectedAmount,
        payment_method: paymentMethod,
      });

      if (res.data.success) {
        const { order_id, qr_code } = res.data.data;
        setOrderId(order_id);
        setQrCode(qr_code);
        setPaymentStatus('pending');
        setCountdown(1800);

        // 开始倒计时
        timerRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // 开始轮询支付状态
        pollTimerRef.current = setInterval(async () => {
          try {
            const statusRes = await API.get(`/api/payment/status/${order_id}`);
            if (statusRes.data.success) {
              const { status } = statusRes.data.data;
              if (status === 1) { // 已支付
                clearInterval(timerRef.current);
                clearInterval(pollTimerRef.current);
                setPaymentStatus('paid');
                showSuccess('充值成功！');
                getUserQuota(); // 刷新余额
                setTimeout(() => {
                  setPaymentModalOpen(false);
                  resetPaymentState();
                }, 2000);
              }
            }
          } catch (err) {
            console.error('查询支付状态失败:', err);
          }
        }, 3000); // 每3秒查询一次
      } else {
        showError(res.data.message || '创建支付订单失败');
      }
    } catch (err) {
      showError('创建支付订单失败: ' + err.message);
    }
  };

  const resetPaymentState = () => {
    setQrCode('');
    setOrderId('');
    setPaymentStatus('pending');
    setCountdown(1800);
    if (timerRef.current) clearInterval(timerRef.current);
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
  };

  // 获取充值历史
  const getPaymentHistory = async () => {
    try {
      const res = await API.get('/api/payment/history');
      if (res.data && res.data.success) {
        setPaymentHistory(res.data.data || []);
      } else {
        console.error('获取充值历史失败:', res.data);
      }
    } catch (err) {
      console.error('获取充值历史失败:', err);
      if (err.response) {
        console.error('Error status:', err.response.status);
        console.error('Error data:', err.response.data);
      }
    }
  };

  const getUserQuota = async () => {
    let res = await API.get(`/api/user/self`);
    const { success, message, data } = res.data;
    if (success) {
      setUserQuota(data.quota);
      setUser(data);
    } else {
      showError(message);
    }
  };

  useEffect(() => {
    let status = localStorage.getItem('status');
    if (status) {
      status = JSON.parse(status);
      if (status.top_up_link) {
        setTopUpLink(status.top_up_link);
      }
    }
    getUserQuota().then();
    getPaymentHistory().then();
  }, []);

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header>
            <Header as='h2'>{t('topup.title')}</Header>
          </Card.Header>

          <Grid columns={2} stackable>
            <Grid.Column>
              <Card
                fluid
                style={{
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Card.Content
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Card.Header>
                    <Header as='h3' style={{ color: '#2185d0', margin: '1em' }}>
                      <i className='credit card icon'></i>
                      {currentLanguage === 'en' ? 'User Quota' : '用户额度'}
                    </Header>
                  </Card.Header>
                  <Card.Description
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div style={{ textAlign: 'center', paddingTop: '1em' }}>
                        <Statistic>
                          <Statistic.Value style={{ color: '#2185d0' }}>
                            {renderQuota(userQuota, t)}
                          </Statistic.Value>
                          <Statistic.Label>
                            {t('topup.get_code.current_quota')}
                          </Statistic.Label>
                        </Statistic>
                      </div>

                      <div
                        style={{ textAlign: 'center', paddingBottom: '1em' }}
                      >
                        <Button
                          primary
                          size='large'
                          onClick={openTopUpLink}
                          style={{ width: '80%' }}
                        >
                          {currentLanguage === 'en' ? 'Contact Customer Service for Redemption Code' : '微信/支付宝充值'}
                        </Button>
                      </div>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>

            <Grid.Column>
              <Card
                fluid
                style={{
                  height: '100%',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                <Card.Content
                  style={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Card.Header>
                    <Header as='h3' style={{ color: '#21ba45', margin: '1em' }}>
                      <i className='ticket alternate icon'></i>
                      {t('topup.redeem_code.title')}
                    </Header>
                  </Card.Header>
                  <Card.Description
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Form.Input
                        fluid
                        icon='key'
                        iconPosition='left'
                        placeholder={t('topup.redeem_code.placeholder')}
                        value={redemptionCode}
                        onChange={(e) => {
                          setRedemptionCode(e.target.value);
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          setRedemptionCode(pastedText.trim());
                        }}
                        action={
                          <Button
                            icon='paste'
                            content={t('topup.redeem_code.paste')}
                            onClick={async () => {
                              try {
                                const text =
                                  await navigator.clipboard.readText();
                                setRedemptionCode(text.trim());
                              } catch (err) {
                                showError(t('topup.redeem_code.paste_error'));
                              }
                            }}
                          />
                        }
                      />

                      <div style={{ paddingBottom: '1em' }}>
                        <Button
                          color='green'
                          fluid
                          size='large'
                          onClick={topUp}
                          loading={isSubmitting}
                          disabled={isSubmitting}
                        >
                          {isSubmitting
                            ? t('topup.redeem_code.submitting')
                            : t('topup.redeem_code.submit')}
                        </Button>
                      </div>
                    </div>
                  </Card.Description>
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>
        </Card.Content>
      </Card>

      {/* 充值历史列表 */}
      {paymentHistory.length > 0 && (
        <Card fluid className='chart-card' style={{ marginTop: '20px' }}>
          <Card.Content>
            <Card.Header>
              <Header as='h3'>充值记录</Header>
            </Card.Header>
            <Card.Content>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ padding: '10px', textAlign: 'left' }}>订单号</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>金额</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>支付方式</th>
                      <th style={{ padding: '10px', textAlign: 'left' }}>时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((order) => (
                      <tr key={order.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px', fontSize: '12px' }}>{order.order_id}</td>
                        <td style={{ padding: '10px', color: '#2185d0', fontWeight: 'bold' }}>¥{order.amount}</td>
                        <td style={{ padding: '10px' }}>
                          {order.payment_method === 'wechat' ? '微信支付' : '支付宝'}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>
                          {new Date(order.created_at).toLocaleString('zh-CN')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card.Content>
          </Card.Content>
        </Card>
      )}

      {/* 充值弹窗 */}
      <Modal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        size='small'
      >
        <Modal.Header>选择充值金额和支付方式</Modal.Header>
        <Modal.Content>
          {/* 金额选择 */}
          <Header as='h4'>充值金额</Header>
          <Segment.Group>
            {amountOptions.map((option) => (
              <Segment
                key={option.value}
                onClick={() => setSelectedAmount(option.value)}
                style={{
                  cursor: 'pointer',
                  border: selectedAmount === option.value ? '2px solid #2185d0' : '1px solid #ddd',
                  background: selectedAmount === option.value ? '#f0f8ff' : '#fff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '18px', color: '#2185d0' }}>{option.label}</strong>
                    <span style={{ marginLeft: '10px', color: '#666' }}>{option.quota}</span>
                  </div>
                  {selectedAmount === option.value && (
                    <span style={{ color: '#2185d0', fontSize: '20px' }}>✓</span>
                  )}
                </div>
              </Segment>
            ))}
          </Segment.Group>

          <Divider />

          {/* 支付方式选择 */}
          <Header as='h4'>支付方式</Header>
          <Segment.Group>
            <Segment
              onClick={() => setPaymentMethod('alipay')}
              style={{
                cursor: 'pointer',
                border: paymentMethod === 'alipay' ? '2px solid #1677ff' : '1px solid #ddd',
                background: paymentMethod === 'alipay' ? '#f0f5ff' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Radio
                  checked={paymentMethod === 'alipay'}
                  onChange={() => setPaymentMethod('alipay')}
                />
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#1677ff">
                  <path d="M21.422 14.752c-1.386-.652-3.199-1.386-5.288-2.082.366-.814.702-1.732.992-2.722h-3.446v-1.46h4.156V7.54h-4.156V5.868h-2.632v1.672h-4.156v.948h4.156v1.46H7.656v.948h6.424c-.26.744-.574 1.46-.926 2.132-1.926-.548-3.996-.652-5.762.026-2.422.932-3.676 3.016-3.362 5.136.314 2.12 2.158 3.494 4.58 3.494 1.34 0 2.612-.368 3.716-1.012 1.484-.864 2.644-2.232 3.33-3.864 1.532.564 2.906 1.208 4.066 1.864.784.444 1.346.924 1.704 1.416.422.582.626 1.218.626 1.88 0 1.476-1.098 2.676-2.454 2.676-1.036 0-1.962-.686-2.468-1.734-.34-.706-.512-1.512-.512-2.366h-2.632c0 1.434.362 2.782 1.014 3.946.914 1.63 2.486 2.786 4.336 2.786 2.998 0 5.428-2.43 5.428-5.428 0-1.582-.682-3.044-1.826-4.076z"/>
                </svg>
                <span style={{ fontSize: '16px', fontWeight: '500' }}>支付宝</span>
              </div>
            </Segment>
            <Segment
              onClick={() => setPaymentMethod('wechat')}
              style={{
                cursor: 'pointer',
                border: paymentMethod === 'wechat' ? '2px solid #07c160' : '1px solid #ddd',
                background: paymentMethod === 'wechat' ? '#f0fff5' : '#fff',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Radio
                  checked={paymentMethod === 'wechat'}
                  onChange={() => setPaymentMethod('wechat')}
                />
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#07c160">
                  <path d="M8.5 11a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm5 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM8.5 2C4.36 2 1 4.92 1 8.5c0 2.03 1.07 3.82 2.76 5l-.7 2.1 2.46-1.24c.84.26 1.74.4 2.68.4.22 0 .43-.01.64-.03A6.47 6.47 0 0 1 8.5 13c0-3.58 3.34-6.5 7.5-6.5.34 0 .67.03 1 .07C16.26 3.96 12.64 2 8.5 2zm7.5 7c-4.14 0-7.5 2.92-7.5 6.5s3.36 6.5 7.5 6.5c.82 0 1.62-.12 2.36-.34L20.5 23l-.58-1.74C21.32 19.88 22.5 18.12 22.5 16c0-3.58-3.36-7-6.5-7zm-2.5 9a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm5 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z"/>
                </svg>
                <span style={{ fontSize: '16px', fontWeight: '500' }}>微信支付</span>
              </div>
            </Segment>
          </Segment.Group>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setPaymentModalOpen(false)}>
            取消
          </Button>
          <Button
            primary
            onClick={handlePayment}
            style={{
              background: paymentMethod === 'alipay' ? '#1677ff' : '#07c160',
            }}
          >
            充值 ¥{selectedAmount}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 二维码显示弹窗 */}
      <Modal
        open={qrCode !== ''}
        onClose={() => {
          resetPaymentState();
        }}
        size='mini'
      >
        <Modal.Header>扫码支付</Modal.Header>
        <Modal.Content style={{ textAlign: 'center' }}>
          <Message info>
            <p>请使用{paymentMethod === 'wechat' ? '微信' : '支付宝'}扫码支付</p>
            <p>金额：<strong>¥{selectedAmount}</strong></p>
          </Message>
          
          {qrCode && (
            <div style={{ margin: '20px 0' }}>
              {/* 使用二维码生成库将qrCode URL转换为二维码图片 */}
              <Image 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCode)}`}
                size='large'
                centered
              />
            </div>
          )}

          <Divider />
          
          <p>倒计时：{Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}</p>
          
          {paymentStatus === 'paid' && (
            <Message success>
              <p>✓ 支付成功！</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => {
            resetPaymentState();
          }}>
            关闭
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default TopUp;
